<template>
  <div class="flex flex-col gap-6">
    <!-- Financial Summary -->
    <section>
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.financial-summary') }}
      </h3>
      <div class="border-b border-n-200 dark:border-n-600 pb-2 mb-2">
        <div class="flex justify-between text-xs text-n-400">
          <span>{{ t('z-report.metric') }}</span>
          <span>{{ t('z-report.amount') }}</span>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.gross-sales') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(grossSales) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.discounts-promotions') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(-report.breakdown.discounts) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">{{ t('z-report.net-sales') }}</span>
          <span class="text-n-800 dark:text-n-0 font-medium">
            {{ currency(report.breakdown.total) }}
          </span>
        </div>
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.tips') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(totalTips) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">
            {{ t('z-report.total-collected') }}
          </span>
          <span class="text-n-800 dark:text-n-0 font-medium">{{ currency(totalCollected) }}</span>
        </div>
      </div>
    </section>

    <!-- Payments Summary -->
    <section v-if="sortedPayments.length > 0">
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.payments-summary') }}
      </h3>
      <div class="border-b border-n-200 dark:border-n-600 pb-2 mb-2">
        <div class="flex justify-between text-xs text-n-400">
          <span>{{ t('z-report.method') }}</span>
          <span>{{ t('z-report.amount') }}</span>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          v-for="payment in sortedPayments"
          :key="payment.method"
          class="flex justify-between py-2 text-sm"
        >
          <span class="text-n-500 dark:text-n-300 capitalize">{{ payment.method }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(payment.amount) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">{{ t('z-report.total') }}</span>
          <span class="text-n-800 dark:text-n-0 font-medium">{{ currency(totalPayments) }}</span>
        </div>
      </div>
    </section>

    <!-- Cash Flow Analysis -->
    <section>
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.cash-flow-analysis') }}
      </h3>
      <div class="border-b border-n-200 dark:border-n-600 pb-2 mb-2">
        <div class="flex justify-between text-xs text-n-400">
          <span>{{ t('z-report.item') }}</span>
          <span>{{ t('z-report.amount') }}</span>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.total-opening-funds') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(totalOpeningFunds) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.total-cash-sales') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(report.payments.cash ?? 0) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.total-pay-ins') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(report.totalIns) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.total-pay-outs') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(-report.totalOuts) }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">
            {{ t('z-report.expected-cash') }}
          </span>
          <span class="text-n-800 dark:text-n-0 font-medium">{{ currency(expectedCash) }}</span>
        </div>
      </div>
    </section>

    <!-- Global Reconciliation -->
    <section v-if="report.shifts && Object.keys(report.shifts).length > 0">
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.global-reconciliation') }}
      </h3>
      <div class="border-b border-n-200 dark:border-n-600 pb-2 mb-2">
        <div class="grid grid-cols-4 text-xs text-n-400">
          <span>{{ t('z-report.method') }}</span>
          <span class="text-right">{{ t('z-report.expected') }}</span>
          <span class="text-right">{{ t('z-report.counted') }}</span>
          <span class="text-right">{{ t('z-report.difference') }}</span>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          v-for="(shift, method) in report.shifts"
          :key="method"
          class="grid grid-cols-4 py-2 text-sm"
        >
          <span class="text-n-500 dark:text-n-300 capitalize">
            {{ te(`bill.${method}`) ? t(`bill.${method}`) : String(method) }}
          </span>
          <span class="text-n-800 dark:text-n-0 text-right">
            {{ currency(shift.expectedEndAmount) }}
          </span>
          <span class="text-n-800 dark:text-n-0 text-right">{{ currency(shift.endAmount) }}</span>
          <span class="text-n-800 dark:text-n-0 text-right">
            {{ currency(shift.endAmount - shift.expectedEndAmount) }}
          </span>
        </div>
        <div class="grid grid-cols-4 py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">{{ t('z-report.total') }}</span>
          <span class="text-n-800 dark:text-n-0 font-medium text-right">
            {{ currency(reconciliationTotals.expected) }}
          </span>
          <span class="text-n-800 dark:text-n-0 font-medium text-right">
            {{ currency(reconciliationTotals.counted) }}
          </span>
          <span class="text-n-800 dark:text-n-0 font-medium text-right">
            {{ currency(reconciliationTotals.difference) }}
          </span>
        </div>
      </div>
    </section>

    <!-- Tax Breakdown -->
    <section v-if="Object.keys(report.breakdown.taxRates).length > 0">
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.tax-breakdown') }}
      </h3>
      <div class="border-b border-n-200 dark:border-n-600 pb-2 mb-2">
        <div class="grid grid-cols-3 text-xs text-n-400">
          <span>{{ t('z-report.tax-rate') }}</span>
          <span class="text-right">{{ t('z-report.net-amount') }}</span>
          <span class="text-right">{{ t('z-report.tax-amount') }}</span>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          v-for="(taxRate, label) in report.breakdown.taxRates"
          :key="label"
          class="grid grid-cols-3 py-2 text-sm"
        >
          <span class="text-n-500 dark:text-n-300">
            {{ t('z-report.vat') }} {{ taxRate.taxPercentage }}%
          </span>
          <span class="text-n-800 dark:text-n-0 text-right">
            {{ currency(taxRate.taxableBase) }}
          </span>
          <span class="text-n-800 dark:text-n-0 text-right">{{ currency(taxRate.tax) }}</span>
        </div>
        <div class="grid grid-cols-3 py-2 text-sm bg-n-100 dark:bg-n-700 -mx-4 px-4 rounded">
          <span class="text-n-800 dark:text-n-0 font-medium">{{ t('z-report.total-vat') }}</span>
          <span class="text-n-800 dark:text-n-0 font-medium text-right">—</span>
          <span class="text-n-800 dark:text-n-0 font-medium text-right">
            {{ currency(report.breakdown.tax) }}
          </span>
        </div>
      </div>
    </section>

    <!-- Operational Details -->
    <section>
      <h3 class="text-n-800 dark:text-n-0 font-heading font-semibold mb-3">
        {{ t('z-report.operational-details') }}
      </h3>
      <div class="flex flex-col">
        <div class="flex justify-between py-2 text-sm border-b border-n-200 dark:border-n-600">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.total-tabs') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ report.stats.totalTabs }}</span>
        </div>
        <div class="flex justify-between py-2 text-sm border-b border-n-200 dark:border-n-600">
          <span class="text-n-500 dark:text-n-300">{{ t('z-report.average-per-tab') }}</span>
          <span class="text-n-800 dark:text-n-0">{{ currency(report.stats.averagePerTab) }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMoney } from '@/composables/useMoney'
import type { ZReport } from '@/types'

const props = defineProps<{
  report: ZReport
}>()

const { t, te } = useI18n()
const { currency } = useMoney()

const grossSales = computed(() => {
  return props.report.breakdown.total + props.report.breakdown.discounts
})

const totalTips = computed(() => {
  return Object.values(props.report.tips).reduce((sum, tip) => sum + tip, 0)
})

const totalCollected = computed(() => {
  return props.report.breakdown.total + totalTips.value
})

const totalOpeningFunds = computed(() => {
  if (!props.report.shifts) return 0
  return Object.values(props.report.shifts).reduce((sum, shift) => sum + shift.startAmount, 0)
})

const expectedCash = computed(() => {
  return (
    totalOpeningFunds.value +
    (props.report.payments.cash ?? 0) +
    props.report.totalIns -
    props.report.totalOuts
  )
})

const reconciliationTotals = computed(() => {
  if (!props.report.shifts) {
    return { expected: 0, counted: 0, difference: 0 }
  }
  const shifts = Object.values(props.report.shifts)
  const expected = shifts.reduce((sum, s) => sum + s.expectedEndAmount, 0)
  const counted = shifts.reduce((sum, s) => sum + s.endAmount, 0)
  return {
    expected,
    counted,
    difference: counted - expected
  }
})

const sortedPayments = computed(() => {
  return Object.keys(props.report.payments)
    .map(method => ({
      method,
      amount: props.report.payments[method]
    }))
    .sort((a, b) => b.amount - a.amount)
})

const totalPayments = computed(() => {
  return Object.values(props.report.payments).reduce((sum, amount) => sum + amount, 0)
})
</script>
